/**
 * The "New customer" form's state and rules, shared by every way in: the
 * `customers/new` page, the catalogue's dialog, a won lead and Promo Office
 * (v1 completion C5, docs/features/v1-completion/C_customer_master_spec.md
 * §2.9). The server owns every field rule; this shapes what is typed into
 * the request, binds the server's field errors to the inputs, and works out
 * the one warning the form gives before anything is sent.
 */

import type {
  AddressCountry,
  CreateCisCustomerRequest,
  FoundAddress,
  LeadDetail,
  NewCustomerForm,
  NewCustomerOwners,
} from "../data/api-types";
import type { FieldError } from "../data/validation";

/** The value an owner select holds for House Account (no person on that axis). */
export const HOUSE_ACCOUNT = "";

export const DEFAULT_COUNTRY_CODE = "GB";

export interface NewCustomerDraft {
  name: string;
  market: string;
  addressLine1: string;
  addressLine2: string;
  town: string;
  postcode: string;
  countryCode: string;
  /** A user id, or `HOUSE_ACCOUNT`. */
  salesRepUserId: string;
  /** A user id, or `HOUSE_ACCOUNT`. */
  csrUserId: string;
  /**
   * The optional "Order contact" step (v1 completion D2): the person new
   * quotes and orders for the customer start with. Left empty, none is sent.
   */
  orderContactName: string;
  orderContactEmail: string;
}

/** An empty form, with the caller's default sales rep and CSR when the form has loaded. */
export function emptyNewCustomerDraft(
  form: NewCustomerForm | null,
  name = "",
): NewCustomerDraft {
  return {
    name,
    market: "",
    addressLine1: "",
    addressLine2: "",
    town: "",
    postcode: "",
    countryCode: DEFAULT_COUNTRY_CODE,
    salesRepUserId: form?.default_sales_rep_user_id ?? HOUSE_ACCOUNT,
    csrUserId: form?.default_csr_user_id ?? HOUSE_ACCOUNT,
    orderContactName: "",
    orderContactEmail: "",
  };
}

/**
 * The form filled from a won lead: its company name and address, the lead's
 * owner as the sales rep when they are one (otherwise the caller's default),
 * and the lead's first contact with an email as the order contact (D2). The
 * lead's free-text country is matched to a country by name; one that matches
 * none stays the default.
 */
export function newCustomerDraftFromLead(
  lead: Pick<LeadDetail, "company_name" | "address" | "owner_user_id"> &
    Partial<Pick<LeadDetail, "contacts">>,
  form: NewCustomerForm | null,
  countries: readonly AddressCountry[],
): NewCustomerDraft {
  const draft = emptyNewCustomerDraft(form, lead.company_name);
  const address = lead.address;
  if (address) {
    const country = countries.find(
      (c) =>
        c.name.toLowerCase() === (address.country ?? "").trim().toLowerCase(),
    );
    Object.assign(draft, {
      addressLine1: address.address_line1 ?? "",
      addressLine2: address.address_line2 ?? "",
      town: address.city ?? "",
      postcode: address.postcode ?? "",
      countryCode: country?.code ?? DEFAULT_COUNTRY_CODE,
    });
  }
  if (
    (form?.sales_rep_options ?? []).some(
      (o) => o.user_id === lead.owner_user_id,
    )
  )
    draft.salesRepUserId = lead.owner_user_id;
  const contact = (lead.contacts ?? []).find((c) => (c.email ?? "").trim());
  if (contact) {
    draft.orderContactName = (contact.name ?? "").trim();
    draft.orderContactEmail = (contact.email ?? "").trim();
  }
  return draft;
}

/** The form after *Find by postcode*: the premise's lines, town and postcode. */
export function fillNewCustomerFromPremise(
  draft: NewCustomerDraft,
  premise: FoundAddress,
): NewCustomerDraft {
  return {
    ...draft,
    addressLine1: premise.lines[0] ?? "",
    addressLine2: premise.lines.slice(1).join(", "),
    town: premise.town ?? "",
    postcode: premise.postcode,
  };
}

function hasAddress(draft: NewCustomerDraft): boolean {
  return [draft.addressLine1, draft.addressLine2, draft.town, draft.postcode]
    .map((value) => value.trim())
    .some((value) => value !== "");
}

export function newCustomerOwners(draft: NewCustomerDraft): NewCustomerOwners {
  return {
    sales_rep_user_id: draft.salesRepUserId || null,
    csr_user_id: draft.csrUserId || null,
  };
}

/**
 * The request the form sends. The address is optional ("add it later on the
 * customer"): left empty it is not sent; started, it is sent whole and the
 * server says what is missing. `withMarket` is off where the form has no
 * market field.
 */
export function toCreateCisCustomerRequest(
  draft: NewCustomerDraft,
  {
    withMarket = true,
    withOrderContact = false,
  }: { withMarket?: boolean; withOrderContact?: boolean } = {},
): CreateCisCustomerRequest {
  return {
    name: draft.name,
    market: withMarket ? draft.market.trim() || null : null,
    account_address: hasAddress(draft)
      ? {
          lines: [draft.addressLine1, draft.addressLine2]
            .map((line) => line.trim())
            .filter((line, index) => index === 0 || line !== ""),
          town: draft.town.trim() || null,
          postcode: draft.postcode,
          country_code: draft.countryCode,
        }
      : null,
    owners: newCustomerOwners(draft),
    order_contact: withOrderContact ? newCustomerOrderContact(draft) : null,
  };
}

/**
 * What the page says when the customer was created but the order contact it
 * asked for wasn't saved (the write failed after the create).
 */
export const ORDER_CONTACT_NOT_SAVED_NOTE =
  "Customer created. The order contact wasn't saved. Set it on the People tab.";

/** The order contact the form sends: none while its email is empty (the step is optional). */
export function newCustomerOrderContact(
  draft: Pick<NewCustomerDraft, "orderContactName" | "orderContactEmail">,
): { name: string; email: string } | null {
  const email = draft.orderContactEmail.trim();
  return email ? { name: draft.orderContactName.trim(), email } : null;
}

/**
 * Whether the caller won't see the customer once it is created: they don't
 * see every customer, and they are neither its sales rep nor its CSR. The
 * form says so before the create, rather than the customer vanishing after.
 */
export function wontSeeNewCustomer(
  form: NewCustomerForm,
  draft: Pick<NewCustomerDraft, "salesRepUserId" | "csrUserId">,
): boolean {
  return (
    !form.caller_sees_every_customer &&
    draft.salesRepUserId !== form.caller_user_id &&
    draft.csrUserId !== form.caller_user_id
  );
}

/** The inputs the form binds field errors to. */
export type NewCustomerField =
  | "name"
  | "market"
  | "addressLines"
  | "town"
  | "postcode"
  | "country"
  | "salesRep"
  | "csr"
  | "orderContactName"
  | "orderContactEmail";

const SERVER_FIELDS: Record<string, NewCustomerField> = {
  name: "name",
  market: "market",
  "account_address.lines": "addressLines",
  "account_address.town": "town",
  "account_address.postcode": "postcode",
  "account_address.country_code": "country",
  "owners.sales_rep_user_id": "salesRep",
  "owners.csr_user_id": "csr",
  sales_rep_user_id: "salesRep",
  csr_user_id: "csr",
  "order_contact.name": "orderContactName",
  "order_contact.email": "orderContactEmail",
};

function inputFor(serverField: string): NewCustomerField | null {
  if (serverField.startsWith("account_address.lines.")) return "addressLines";
  return SERVER_FIELDS[serverField] ?? null;
}

/**
 * The server's field errors, by the input each belongs next to. A field the
 * form has no input for is kept in `unbound` for the banner, so nothing the
 * server said goes unshown. `count` is every error, for the banner's
 * summary.
 */
export function bindNewCustomerErrors(errors: readonly FieldError[]): {
  byField: Partial<Record<NewCustomerField, string>>;
  unbound: string[];
  count: number;
} {
  const byField: Partial<Record<NewCustomerField, string>> = {};
  const unbound: string[] = [];
  for (const error of errors) {
    const field = inputFor(error.field);
    if (field === null) {
      unbound.push(error.message);
      continue;
    }
    byField[field] = byField[field]
      ? `${byField[field]} ${error.message}`
      : error.message;
  }
  return { byField, unbound, count: errors.length };
}

/** "1 field needs a fix: see below." / "2 fields need a fix: see below." */
export function fieldsNeedAFix(count: number): string {
  return count === 1
    ? "1 field needs a fix: see below."
    : `${count} fields need a fix: see below.`;
}
